/**
 * Pestaña Armador: a qué destino se arma según CÓMO vino la recepción, y qué
 * rinde cada opción. Es la lectura clásica de scouting ("con # reparte, con −
 * se le cae todo al punta"), y sólo se ve condicionando por grado.
 *
 * Sólo entran los grados que dejan armar (#, +, !, −): con `/` no hay opciones
 * y con `=` el punto ya es del rival.
 *
 * ## Las dos fases
 *
 * El selector de arriba elige qué se está mirando:
 *
 *  - **Todo** — la tabla de siempre, sideout + transición juntos.
 *  - **Primera intención** (`sideout`) — el ataque que sale de la recepción:
 *    están todas las opciones abiertas y el bloqueo rival todavía no leyó.
 *  - **Contraataque** (`transition`) — el rearmado tras defensa propia, donde
 *    suele quedar una sola opción. Incluye los rallies que el equipo SACÓ, que
 *    en la tabla por grado no aparecen (no hay recepción propia que
 *    condicionar): por eso cada fase muestra sus dos totales.
 *
 * Un ataque cae en una fase y sólo una, así que los dos totales suman todos los
 * ataques del equipo.
 */
import { useState } from 'react';
import { StackedBar } from '../../components/Charts';
import { Court, heatTone } from '../../components/Court';
import { Card } from '../../components/States';
import type { MessageKey, TFunction } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { effPct, pct } from '../../lib/format';
import {
  SETTABLE_GRADES,
  SETTER_COURT_GRADES,
  type SetterCourt,
  type SetterCourtGrade,
  type SetterCourtLine,
  type TeamSetterCourt,
  type ReceptionImpact,
  type SetterDistribution,
  type SetterGradeLine,
  type SetterPhase,
  type SetterPhaseDistribution,
  type TeamReceptionImpact,
  type TeamSetterDistribution,
} from '../../lib/projections';

const GRADE_KEYS: Record<string, MessageKey> = {
  '#': 'setter.gradePerfect',
  '+': 'setter.gradePositive',
  '!': 'setter.gradeNeutral',
  '-': 'setter.gradeNegative',
};

/** Qué se está mirando: las dos fases juntas o una sola. */
type PhaseView = 'all' | SetterPhase;

const PHASE_VIEWS: readonly PhaseView[] = ['all', 'sideout', 'transition'];

const PHASE_LABELS: Record<PhaseView, MessageKey> = {
  all: 'setter.phaseAll',
  sideout: 'setter.phaseSideout',
  transition: 'setter.phaseTransition',
};

const PHASE_HINTS: Record<PhaseView, MessageKey> = {
  all: 'setter.phaseAllHint',
  sideout: 'setter.phaseSideoutHint',
  transition: 'setter.phaseTransitionHint',
};

/** La tabla por grado que corresponde a la vista elegida. */
function gradesOf(team: TeamSetterDistribution, view: PhaseView): Record<string, SetterGradeLine> {
  if (view === 'all') return team.byReceptionGrade;
  return team[view].byReceptionGrade;
}

/** Una tabla de destinos (se usa por grado y para el total de la fase). */
function TargetTable({ line }: { line: SetterGradeLine }): JSX.Element | null {
  const t = useT();
  if (line.byTarget.length === 0) return null;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('setter.colTarget')}</th>
            <th>{t('setter.colSource')}</th>
            <th className="num">{t('setter.colAttacks')}</th>
            <th className="num">{t('setter.colPercent')}</th>
            <th className="num">{t('setter.colEfficiency')}</th>
          </tr>
        </thead>
        <tbody>
          {line.byTarget.map((target) => (
            <tr key={target.key}>
              <td>{target.key === 'unknown' ? t('setter.uncoded') : target.key}</td>
              <td className="hint">{sourceLabel(target.source, t)}</td>
              <td className="num">{target.count}</td>
              <td className="num">{pct(target.pct)}</td>
              <td className={`num ${target.attackEfficiency < 0 ? 'neg' : ''}`}>
                {effPct(target.attackEfficiency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Cabecera de una fase: sus DOS totales. La diferencia entre "ataques de la
 * fase" y "los que entran en la tabla por grado" no es un bug: en transición
 * son los contraataques de los rallies que el equipo sacó, que antes no
 * aparecían en ningún lado.
 */
function PhaseSummary({ phase }: { phase: SetterPhaseDistribution }): JSX.Element {
  const t = useT();
  const withoutReception = phase.total - phase.withReception;
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="row" style={{ gap: 10, marginBottom: 8 }}>
        <span className="chip muted">{t('setter.phaseTotal', { count: phase.total })}</span>
        <span className="chip muted" title={t('setter.phaseWithReceptionTitle')}>
          {t('setter.phaseWithReception', { count: phase.withReception })}
        </span>
        {withoutReception > 0 ? (
          <span className="chip muted" title={t('setter.phaseWithoutReceptionTitle')}>
            {t('setter.phaseWithoutReception', { count: withoutReception })}
          </span>
        ) : null}
      </div>
      {phase.total === 0 ? (
        <p className="empty">{t('setter.phaseEmpty')}</p>
      ) : (
        <>
          <h3>{t('setter.phaseAllTargets')}</h3>
          <StackedBar
            segments={phase.byTarget.map((target) => ({
              key: target.key,
              value: target.count,
              title: t('setter.segmentTitle', {
                target: target.key,
                count: target.count,
                pct: pct(target.pct),
                efficiency: effPct(target.attackEfficiency),
              }),
            }))}
          />
          <TargetTable line={{ total: phase.total, byTarget: phase.byTarget }} />
        </>
      )}
    </div>
  );
}

function TeamSetter({
  team,
  side,
  view,
  court,
}: {
  team: TeamSetterDistribution;
  side: 'home' | 'away';
  view: PhaseView;
  court: TeamSetterCourt;
}): JSX.Element {
  const t = useT();
  const grades = gradesOf(team, view);
  const totalAttacks = SETTABLE_GRADES.reduce(
    (sum, grade) => sum + (grades[grade]?.total ?? 0),
    0,
  );

  return (
    <Card
      title={<span className={`side-name ${side} team-title`}>{team.name}</span>}
      actions={
        <span className="chip muted">{t('setter.attacksSet', { count: totalAttacks })}</span>
      }
    >
      <SetterCourts court={court} side={side} view={view} />

      {/* En una fase concreta el encabezado dice cuántos ataques hubo EN TOTAL,
          que es el número que la tabla por grado no puede mostrar. */}
      {view === 'all' ? null : <PhaseSummary phase={team[view]} />}

      {totalAttacks === 0 ? (
        <p className="empty">{t(view === 'all' ? 'setter.empty' : 'setter.phaseNoGrades')}</p>
      ) : (
        SETTABLE_GRADES.map((grade) => {
          const line = grades[grade];
          if (!line) return null;
          return (
            <div key={grade} style={{ marginBottom: 18 }}>
              <h3>
                {t('setter.receptionHeading', {
                  grade,
                  name: GRADE_KEYS[grade] ? t(GRADE_KEYS[grade]) : '',
                  count: line.total,
                })}
              </h3>
              <StackedBar
                segments={line.byTarget.map((target) => ({
                  key: target.key,
                  value: target.count,
                  title: t('setter.segmentTitle', {
                    target: target.key,
                    count: target.count,
                    pct: pct(target.pct),
                    efficiency: effPct(target.attackEfficiency),
                  }),
                }))}
              />
              <TargetTable line={line} />
            </div>
          );
        })
      )}
    </Card>
  );
}

function sourceLabel(source: string, t: TFunction): string {
  switch (source) {
    case 'combo':
      return t('setter.sourceCombo');
    case 'startZone':
      return t('setter.sourceStartZone');
    case 'endZone':
      return t('setter.sourceEndZone');
    default:
      return t('setter.sourceUnknown');
  }
}

/**
 * Complemento obligado de la distribución: cuánto sideout produce cada grado
 * de recepción. Es el número que justifica todo lo demás.
 */
function ReceptionCard({ team, side }: { team: TeamReceptionImpact; side: 'home' | 'away' }): JSX.Element {
  const t = useT();
  return (
    <Card
      title={
        <span className={`side-name ${side}`}>
          {t('setter.receptionCardTitle', { team: team.name })}
        </span>
      }
    >
      {team.total.rallies === 0 ? (
        <p className="empty">{t('setter.noReceptions')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('setter.colGrade')}</th>
                <th className="num">{t('setter.colRallies')}</th>
                <th className="num">{t('setter.colSideout')}</th>
                <th className="num">{t('setter.colSideoutPct')}</th>
              </tr>
            </thead>
            <tbody>
              {team.byEvaluation.map((line) => (
                <tr key={line.evaluation}>
                  <td>{line.evaluation}</td>
                  <td className="num">{line.rallies}</td>
                  <td className="num">{line.sideoutWon}</td>
                  <td className="num">{line.rallies > 0 ? pct(line.sideoutPct) : '·'}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>{t('common.total')}</td>
                <td className="num">{team.total.rallies}</td>
                <td className="num">{team.total.sideoutWon}</td>
                <td className="num">{pct(team.total.sideoutPct)}</td>
              </tr>
            </tbody>
          </table>
          {/*
            Esta tabla condiciona al grado de recepción, así que su denominador
            son los rallies CON recepción codificada — no todos los recibidos,
            que es lo que mide el Sideout% del resumen y de rotaciones. Los dos
            números son correctos y distintos; sin esta línea el mismo rótulo
            aparecía en tres pantallas queriendo decir dos cosas.
          */}
          <p className="hint">{t('setter.receptionCardHint')}</p>
        </div>
      )}
    </Card>
  );
}

export function SetterTab({
  distribution,
  reception,
  court,
}: {
  distribution: SetterDistribution;
  reception: ReceptionImpact;
  /** La misma muestra por zona de salida (`buildSetterCourt`). */
  court: SetterCourt;
}): JSX.Element {
  const t = useT();
  // La fase es estado de la pestaña y no del `MatchPage`: no cambia el dato que
  // se derivó del log, sólo cuál de las tres tablas ya calculadas se dibuja.
  const [view, setView] = useState<PhaseView>('all');

  return (
    <div>
      <div className="row" style={{ gap: 10, marginTop: 0, marginBottom: 10 }}>
        <label className="row" style={{ gap: 6 }}>
          <span className="hint">{t('setter.phaseFilter')}</span>
          <select
            value={view}
            onChange={(e) => setView(e.target.value as PhaseView)}
            style={{ width: 'auto' }}
          >
            {PHASE_VIEWS.map((name) => (
              <option key={name} value={name}>
                {t(PHASE_LABELS[name])}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        {t(PHASE_HINTS[view])} {t('setter.intro')}
      </p>
      <TeamSetter team={distribution.home} side="home" view={view} court={court.home} />
      <TeamSetter team={distribution.away} side="away" view={view} court={court.away} />
      <div className="grid-2">
        <ReceptionCard team={reception.home} side="home" />
        <ReceptionCard team={reception.away} side="away" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// La distribución sobre la cancha (R# / R+ por zona)
// ---------------------------------------------------------------------------

/**
 * Cinco canchas chicas por equipo: todas las recepciones y una por grado
 * (`#`, `+`, `!`, `-`). Cada zona dice qué parte de los ataques salió de ahí,
 * con su eficiencia, y se pinta con la rampa del heatmap. Es la lectura de un
 * vistazo que en la tabla cuesta: "con R# reparte, con R− todo al 4".
 */
function SetterCourts({
  court,
  side,
  view,
}: {
  court: TeamSetterCourt;
  side: 'home' | 'away';
  view: PhaseView;
}): JSX.Element {
  const t = useT();
  const lines = court.views[view];
  return (
    <div style={{ marginBottom: 18 }}>
      <h3>{t('matchViews.setterCourtTitle')}</h3>
      <div className="court-row">
        {SETTER_COURT_GRADES.map((grade) => (
          <SetterCourtFigure
            key={grade}
            line={lines[grade]}
            grade={grade}
            side={side}
            team={court.name}
          />
        ))}
      </div>
      {lines.all.other > 0 ? (
        <p className="court-note">{t('matchViews.setterCourtOther', { count: lines.all.other })}</p>
      ) : null}
    </div>
  );
}

function SetterCourtFigure({
  line,
  grade,
  side,
  team,
}: {
  line: SetterCourtLine;
  grade: SetterCourtGrade;
  side: 'home' | 'away';
  team: string;
}): JSX.Element {
  const t = useT();
  const caption =
    grade === 'all' ? t('matchViews.setterCourtAll') : t('matchViews.setterCourtGrade', { grade });
  const max = Math.max(0, ...line.zones.map((z) => z.share));
  const zones = line.zones.map((z) => ({
    zone: z.zone,
    value: pct(z.share),
    sub: t('matchViews.zoneSub', { count: z.count, eff: effPct(z.efficiency) }),
    ...heatTone(z.share, 0, max),
    title: t('matchViews.setterZoneTitle', {
      zone: z.zone,
      count: z.count,
      pct: pct(z.share),
      eff: effPct(z.efficiency),
    }),
  }));
  return (
    <figure className="court-fig">
      <figcaption>
        {caption}
        <small>{t('matchViews.attacksCount', { count: line.total })}</small>
      </figcaption>
      <Court
        label={t('matchViews.setterCourtLabel', { team, what: caption })}
        sides={[{ team: side, zones, zonePrefix: 'Z' }]}
        width={grade === 'all' ? 230 : 170}
      />
    </figure>
  );
}
